/* ==========================================================================
   Poké Summer Party — pannable board runtime.

   Vanilla port of the DCLogic component from the Claude Design source. The
   pan maths (bounds, rubber-band, inertia decay, jump easing) are carried over
   verbatim so motion feels identical.

   Deliberately NOT ported:
     - card dragging: startDrag() returned on its first line in the source, so
       every card-drag path was unreachable. Only canvas panning was ever live.
     - per-card labels: showLabel evaluated false for all five cards.
     - the progress counter: it referenced an element that isn't in the markup.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.psp-root');
  if (!root) return;

  var viewport = root.querySelector('.kc-viewport');
  var canvas = root.querySelector('.kc-canvas');
  var hint = root.querySelector('.kc-hint');
  var resetBtn = root.querySelector('.icon-btn');
  var slots = [].slice.call(root.querySelectorAll('.kc-slot'));
  if (!viewport || !canvas) return;

  var CANVAS_W = 1900;
  var CANVAS_H = 1700;
  var MARGIN = 120;
  var RUBBER = 70;

  var vw = window.innerWidth;
  var vh = window.innerHeight;

  // Frame the hero: centred horizontally, top near the top of the viewport.
  // heroCX/heroTop are the source's constants, kept as-is so the opening
  // camera position matches the design exactly.
  var heroCX = 700 + 480 / 2;
  var heroTop = 110;
  var tx = -(heroCX - vw / 2);
  var ty = -(heroTop - 130);
  var homeX = tx;
  var homeY = ty;

  var panning = false;
  var lastX = 0, lastY = 0, lastT = 0, velX = 0, velY = 0;
  var rafId = null;

  function bounds() {
    return {
      minX: -(CANVAS_W - vw + MARGIN),
      maxX: MARGIN,
      minY: -(CANVAS_H - vh + MARGIN),
      maxY: MARGIN
    };
  }

  // Exponential resistance once past the edge, so the board feels elastic
  // rather than hard-stopping.
  function rubber(v, min, max) {
    if (v < min) { var o = min - v; return min - RUBBER * (1 - Math.exp(-o / RUBBER)); }
    if (v > max) { var u = v - max; return max + RUBBER * (1 - Math.exp(-u / RUBBER)); }
    return v;
  }

  function render() {
    var b = bounds();
    var dx = rubber(tx, b.minX, b.maxX);
    var dy = rubber(ty, b.minY, b.maxY);
    canvas.style.transform = 'translate3d(' + dx + 'px, ' + dy + 'px, 0)';
  }

  function settle() {
    var b0 = bounds();
    if (!(tx < b0.minX || tx > b0.maxX || ty < b0.minY || ty > b0.maxY)) return;
    var step = function () {
      var b = bounds();
      var cx = Math.min(b.maxX, Math.max(b.minX, tx));
      var cy = Math.min(b.maxY, Math.max(b.minY, ty));
      tx += (cx - tx) * 0.18;
      ty += (cy - ty) * 0.18;
      render();
      if (Math.abs(tx - cx) > 0.5 || Math.abs(ty - cy) > 0.5) {
        requestAnimationFrame(step);
      } else {
        tx = cx; ty = cy; render();
      }
    };
    requestAnimationFrame(step);
  }

  function hideHint() { if (hint) hint.style.opacity = '0'; }

  function startPan(x, y) {
    panning = true;
    viewport.classList.add('is-panning');
    lastX = x; lastY = y; lastT = performance.now();
    velX = 0; velY = 0;
    cancelAnimationFrame(rafId);
    hideHint();
  }

  function movePan(x, y) {
    if (!panning) return;
    var now = performance.now();
    var dt = Math.max(now - lastT, 1);
    var b = bounds();
    var dx = x - lastX;
    var dy = y - lastY;
    // Halve the response past the edge to reinforce the rubber-band feel.
    if (tx < b.minX || tx > b.maxX) dx *= 0.4;
    if (ty < b.minY || ty > b.maxY) dy *= 0.4;
    tx += dx; ty += dy;
    velX = dx / dt; velY = dy / dt;
    lastX = x; lastY = y; lastT = now;
    render();
  }

  function endPan() {
    if (!panning) return;
    panning = false;
    viewport.classList.remove('is-panning');
    var vX = velX * 16;
    var vY = velY * 16;
    var glide = function () {
      vX *= 0.92; vY *= 0.92;
      tx += vX; ty += vY;
      render();
      if (Math.abs(vX) > 0.15 || Math.abs(vY) > 0.15) {
        rafId = requestAnimationFrame(glide);
      } else {
        settle();
      }
    };
    glide();
  }

  var easeOutCubic = function (t) { return 1 - Math.pow(1 - t, 3); };

  function glideTo(destX, destY, done) {
    cancelAnimationFrame(rafId);
    var startX = tx, startY = ty, startTime = performance.now(), duration = 550;
    var animate = function () {
      var t = Math.min(1, (performance.now() - startTime) / duration);
      var e = easeOutCubic(t);
      tx = startX + (destX - startX) * e;
      ty = startY + (destY - startY) * e;
      render();
      if (t < 1) { rafId = requestAnimationFrame(animate); }
      else if (done) { done(); }
    };
    animate();
  }

  function setActive(id) {
    root.querySelectorAll('.kc-dock-btn').forEach(function (b) {
      b.classList.toggle('is-active', !!id && b.getAttribute('data-target') === id);
      b.setAttribute('aria-current', !!id && b.getAttribute('data-target') === id ? 'true' : 'false');
    });
  }

  function flash(el, ms) {
    if (!el) return;
    el.classList.add('jump-highlight');
    setTimeout(function () { el.classList.remove('jump-highlight'); }, ms);
  }

  function jumpTo(id) {
    var el = document.getElementById(id);
    if (!el) return;
    setActive(id);
    hideHint();
    var b = bounds();
    var destX = Math.min(b.maxX, Math.max(b.minX, -(el.offsetLeft + el.offsetWidth / 2 - vw / 2)));
    var destY = Math.min(b.maxY, Math.max(b.minY, -(el.offsetTop + el.offsetHeight / 2 - vh / 2)));
    glideTo(destX, destY, function () { flash(el, 1800); });
  }

  function onReset() {
    if (resetBtn) {
      resetBtn.classList.add('spinning');
      setTimeout(function () { resetBtn.classList.remove('spinning'); }, 500);
    }
    setActive(null);
    glideTo(homeX, homeY);
    slots.forEach(function (el) { flash(el, 800); });
  }

  /* ---- events ---------------------------------------------------------- */

  // Anything interactive swallows the pan so a tap doesn't start a drag.
  var NO_PAN = '.icon-btn, button, a, .kc-video-slot, .mil-cta, .kc-cta-btn';

  viewport.addEventListener('pointerdown', function (e) {
    if (e.target.closest && e.target.closest(NO_PAN)) return;
    startPan(e.clientX, e.clientY);
  });
  window.addEventListener('pointermove', function (e) { movePan(e.clientX, e.clientY); });
  window.addEventListener('pointerup', endPan);
  window.addEventListener('pointercancel', endPan);
  window.addEventListener('resize', function () {
    vw = window.innerWidth;
    vh = window.innerHeight;
    render();
  });

  root.querySelectorAll('.kc-dock-btn').forEach(function (btn) {
    btn.addEventListener('click', function () { jumpTo(btn.getAttribute('data-target')); });
  });
  if (resetBtn) resetBtn.addEventListener('click', onReset);

  var milCta = root.querySelector('.mil-cta');
  if (milCta) {
    milCta.addEventListener('click', function (e) { e.preventDefault(); jumpTo('slot-military'); });
  }

  // iOS strips a prefilled body from a static sms: href, so the URL is built
  // at click time instead of being baked into the markup.
  var smsBtn = root.querySelector('[data-sms-body]');
  if (smsBtn) {
    smsBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var body = encodeURIComponent(smsBtn.getAttribute('data-sms-body') || '');
      var sep = /iP(hone|ad|od)|Mac/.test(navigator.userAgent) ? '&' : '?';
      window.location.href = 'sms:' + sep + 'body=' + body;
    });
  }

  /* ---- optional hero video --------------------------------------------- */
  var video = root.querySelector('.kc-video-slot video');
  var unmute = root.querySelector('.kc-unmute');
  if (video && unmute) {
    var sync = function () {
      var on = !video.muted && video.volume > 0;
      unmute.classList.toggle('is-unmuted', on);
      unmute.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = unmute.querySelector('.label');
      if (label) label.textContent = on ? 'Mute' : 'Tap for sound';
    };
    unmute.addEventListener('click', function (e) {
      e.stopPropagation();
      video.muted = !video.muted;
      if (!video.muted) {
        video.volume = 1;
        if (video.paused) video.play().catch(function () {});
      }
      sync();
    });
    video.addEventListener('volumechange', sync);
    video.addEventListener('loadeddata', sync);
    sync();
  }

  render();
})();
